import { useEffect, useState } from "react";
import { useParams, useLocation } from "wouter";
import { useGetTenantByShortCode, useAddViaJoin } from "@orbit/api-client-react";
import { useAuth } from "../hooks/useAuth";
import { AuthLayout } from "../components/AuthLayout";

export function JoinPage() {
  const params = useParams<{ tenantShortCode: string }>();
  const shortCode = params.tenantShortCode;
  const [, navigate] = useLocation();
  const { isAuthenticated } = useAuth();
  const tenantQ = useGetTenantByShortCode(shortCode);
  const addMut = useAddViaJoin();
  // Phase 3 edge case (h): when the customer re-scans a Join QR for a
  // tenant they're already enrolled with, the API responds with status
  // "already_enrolled". Surface a friendly message instead of bouncing
  // them straight to /cards with no acknowledgement.
  const [alreadyMember, setAlreadyMember] = useState(false);

  // Reset the "already a member" flag whenever the route's shortCode
  // changes — wouter sometimes reuses the component instance across
  // /j/:shortCode → /j/:differentShortCode transitions, and we don't
  // want a stale message from the previous tenant to leak through.
  useEffect(() => {
    setAlreadyMember(false);
  }, [shortCode]);

  useEffect(() => {
    if (!addMut.isSuccess || !addMut.data) return;
    if (addMut.data.status === "already_enrolled") {
      setAlreadyMember(true);
      return;
    }
    navigate("/cards");
  }, [addMut.isSuccess, addMut.data, navigate]);

  // D90 — Render a loading skeleton instead of `null` for any state
  // where we don't yet have tenant data. Three known paths land here:
  //   (1) tenantQ.isLoading — query in flight
  //   (2) tenantQ is idle because shortCode is briefly undefined
  //       (wouter + React 18 sometimes returns empty params on the
  //       first render after a client-side navigate; the next render
  //       has the right value)
  //   (3) tenantQ resolved with no data (shouldn't happen, but the old
  //       `if (!tenant) return null` painted a permanent blank screen
  //       in this case, requiring a hard refresh to recover — exactly
  //       the symptom reported after the in-app QR scanner navigated
  //       to /j/<shortCode>)
  // A loading skeleton on the first frame is harmless if data is
  // already cached (it flashes briefly) but eliminates the blank-
  // screen failure mode entirely.
  if (tenantQ.isLoading || !shortCode || (!tenantQ.isError && !tenantQ.data)) {
    return (
      <AuthLayout>
        <p className="text-center text-ink-dark">Loading…</p>
      </AuthLayout>
    );
  }

  if (tenantQ.isError) {
    const status = tenantQ.error?.status;
    return (
      <AuthLayout title="Merchant not found">
        <p className="text-center text-ink-dark">
          {status === 404
            ? "This merchant code doesn't exist or is no longer active."
            : "Couldn't load merchant. Try again in a moment."}
        </p>
      </AuthLayout>
    );
  }

  const tenant = tenantQ.data;
  // Belt-and-braces: with the loading guard above, this should never
  // fire. Kept as a typesafe narrow rather than a `!` assertion.
  if (!tenant) {
    return (
      <AuthLayout>
        <p className="text-center text-ink-dark">Loading…</p>
      </AuthLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <AuthLayout title={tenant.name} subtitle="Joining as a new member of">
        <div className="flex flex-col gap-3">
          <button
            className="btn-primary"
            onClick={() => navigate(`/signup?tenant=${encodeURIComponent(shortCode)}`)}
          >
            Sign up
          </button>
          <button
            className="btn-secondary"
            onClick={() => navigate(`/login?tenant=${encodeURIComponent(shortCode)}`)}
          >
            I already have an account
          </button>
        </div>
      </AuthLayout>
    );
  }

  if (alreadyMember) {
    return (
      <AuthLayout title={`You're already a member of ${tenant.name}!`}>
        <p className="text-center text-ink-dark">
          No need to join again — your card is on the Cards tab.
        </p>
        <button
          className="btn-primary w-full mt-4"
          onClick={() => navigate("/cards")}
        >
          Go to my cards
        </button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={`Add ${tenant.name}?`} subtitle="to your Orbit account">
      {addMut.isError && (
        <p className="text-center text-red-600 mb-3">{addMut.error?.message}</p>
      )}
      <button
        className="btn-primary w-full"
        disabled={addMut.isPending}
        onClick={() => addMut.mutate({ tenantShortCode: shortCode })}
      >
        {addMut.isPending ? "Adding…" : "Add this merchant"}
      </button>
      <button className="btn-secondary w-full mt-3" onClick={() => navigate("/cards")}>
        Not now
      </button>
    </AuthLayout>
  );
}
